import React, { FC, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import * as eCharts from 'echarts';
import type { ECElementEvent, SeriesOption, GridComponentOption, DataZoomComponentOption, TooltipComponentOption, XAXisComponentOption, YAXisComponentOption, LegendComponentOption, DatasetComponentOption } from 'echarts';
import { isPresent } from '@farmai-shared/LFUtils';
export const LFEChart: FC<{
  series: SeriesOption | SeriesOption[];
  tooltip?: TooltipComponentOption;
  dataZoom?: DataZoomComponentOption | DataZoomComponentOption[];
  grid?: GridComponentOption | GridComponentOption[];
  xAxis?: XAXisComponentOption | XAXisComponentOption[];
  yAxis?: YAXisComponentOption | YAXisComponentOption[];
  events?: {
    eventName: 'click' | 'mouseover';
    action: (e: ECElementEvent) => void;
  }[];
  legend?: LegendComponentOption;
  dataset?: DatasetComponentOption;
  mapLayerType?: string;
  onChartInit?: (chart: eCharts.ECharts) => void;
}> = observer(({
  series,
  tooltip,
  dataZoom,
  grid,
  xAxis,
  yAxis,
  events,
  dataset,
  legend,
  onChartInit
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [chartInstance, setChartInstance] = useState<eCharts.ECharts | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  useEffect(() => {
    const mediaQueryList = window.matchMedia('print');
    const updateChartForPrint = (printMode: boolean) => {
      if (!chartInstance) return;
      chartInstance.setOption({
        dataZoom: [{
          show: !printMode,
          type: printMode ? 'inside' : 'slider',
          filterMode: 'empty',
          showDetail: false,
          ...(dataZoom || {})
        }]
      }, false);
    };
    const updateIsPrinting = (e: MediaQueryListEvent) => {
      updateChartForPrint(e.matches);
    };
    const handleBeforePrint = () => {
      updateChartForPrint(true);
    };
    const handleAfterPrint = () => {
      updateChartForPrint(false);
    };
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', updateIsPrinting);
    }
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', updateIsPrinting);
      }
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [chartInstance, dataZoom]);
  const dispose = () => {
    if (chartInstance) {
      chartInstance.dispose();
      setChartInstance(null);
    }
  };
  const bindEvents = (instance: eCharts.ECharts, events: {
    eventName: 'click' | 'mouseover';
    action: (e: ECElementEvent) => void;
  }[]) => {
    events.forEach(e => {
      instance.on(e.eventName, e.action);
    });
  };
  const resize = () => {
    if (chartInstance && hasLoaded) {
      chartInstance.resize();
    }
  };
  const onLoaded = () => setHasLoaded(true);
  useEffect(() => {
    if (ref.current) {
      const chart = eCharts.init(ref.current);
      setChartInstance(chart);
    }
    return () => dispose();
  }, [ref.current]);
  useEffect(() => {
    if (chartInstance) {
      const option = {
        series: series,
        ...(dataset && {
          dataset: dataset
        }),
        ...(tooltip && {
          tooltip: tooltip
        }),
        ...(dataZoom && {
          dataZoom: [{
            showDataShadow: false,
            position: ['center', '90%'],
            ...dataZoom
          }]
        }),
        ...(grid && {
          grid: grid
        }),
        ...(xAxis && {
          xAxis: xAxis
        }),
        ...(yAxis && {
          yAxis: yAxis
        }),
        ...(legend && {
          legend: legend
        }),
        textStyle: {
          fontFamily: 'Kanit'
        }
      };
      chartInstance.setOption(option, {
        replaceMerge: ['series', 'dataset', 'xAxis', 'yAxis']
      });
      chartInstance.on('finished', onLoaded);
      if (isPresent(events)) {
        bindEvents(chartInstance, events);
      }
      if (onChartInit) {
        onChartInit(chartInstance);
      }
    }
    return () => {
      if (chartInstance) {
        chartInstance.off('finished', onLoaded);
        if (isPresent(events)) {
          events.forEach(e => chartInstance.off(e.eventName, e.action));
        }
      }
    };
  }, [series, tooltip, dataZoom, grid, xAxis, yAxis, dataset, legend, chartInstance]);
  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [hasLoaded, chartInstance]);
  return <div ref={ref} className={`h-full w-full`} />;
});